import React, { useState, useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Hook to detect mobile screen
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return isMobile;
};

function InvoiceScanner({ token }) {
  const isMobile = useIsMobile();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [repairId, setRepairId] = useState('');
  const [repairs, setRepairs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetchWorkCompleteRepairs();
  }, []);

  const fetchWorkCompleteRepairs = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/approvals/work-complete`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRepairs(data);
      }
    } catch (error) {
      console.error('Error fetching repairs:', error);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    setResult(null);
    
    if (selectedFile) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(selectedFile);
    } else {
      setPreview(null);
    }
  };

  const handleScan = async (e) => {
    e.preventDefault();
    if (!file) return;

    setLoading(true);
    setResult(null);

    try {
      const formData = new FormData();
      if (repairId) formData.append('repair_id', repairId);
      formData.append('invoice_file', file);

      const response = await fetch(`${BACKEND_URL}/api/invoices/scan`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json();
      
      if (response.ok) {
        setResult(data);
      } else {
        setResult({ error: data.detail || 'Scan failed' });
      }
    } catch (error) {
      setResult({ error: error.message });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('en-SG', { style: 'currency', currency: 'SGD' }).format(amount);
  };

  const handleExportExcel = async () => {
    if (!repairId) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/invoices/${repairId}/export-excel`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();

      const link = document.createElement('a');
      link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${data.excel}`;
      link.download = data.filename;
      link.click();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const data = result?.extracted_data;

  return (
    <div data-testid="invoice-scanner-page">
      <div className="page-header">
        <h2 style={isMobile ? { fontSize: '1.25rem' } : {}}>Invoice Scanner</h2>
        <p style={isMobile ? { fontSize: '0.8rem' } : {}}>AI-powered OCR to extract data from invoices</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1.5rem' }}>
        {/* Upload Section */}
        <div className="card" style={isMobile ? { padding: '1rem' } : {}}>
          <h3 className="card-title" style={isMobile ? { fontSize: '1rem' } : {}}>Upload Invoice</h3>
          <form onSubmit={handleScan} data-testid="invoice-scan-form">
            <div className="form-group">
              <label className="form-label">Link to Repair (Optional)</label>
              <select
                className="form-select"
                data-testid="repair-id-input"
                value={repairId}
                onChange={(e) => setRepairId(e.target.value)}
                style={isMobile ? { fontSize: '0.85rem' } : {}}
              >
                <option value="">-- Select repair --</option>
                {repairs.map(r => (
                  <option key={r.repair_id} value={r.repair_id}>
                    {r.vehicle_no} - {r.repair?.description?.substring(0, isMobile ? 20 : 40)}... ({formatCurrency(r.estimated_cost)})
                  </option>
                ))}
              </select>
              {!isMobile && (
                <p style={{ fontSize: '0.8rem', color: 'var(--gray-500)', marginTop: '0.25rem' }}>
                  Select a work-complete repair to compare invoice against approved quote
                </p>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Invoice Image/PDF</label>
              <div 
                className="drop-zone"
                style={{ 
                  position: 'relative',
                  minHeight: preview ? '300px' : '150px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <input
                  type="file"
                  data-testid="invoice-file-input"
                  accept="image/*,application/pdf"
                  onChange={handleFileChange}
                  style={{ 
                    position: 'absolute', 
                    inset: 0, 
                    opacity: 0, 
                    cursor: 'pointer' 
                  }}
                />
                {preview ? (
                  <img 
                    src={preview} 
                    alt="Invoice preview" 
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: '280px', 
                      objectFit: 'contain',
                      borderRadius: '8px'
                    }} 
                  />
                ) : (
                  <>
                    <div style={{ fontSize: '3rem', marginBottom: '0.5rem', opacity: 0.5 }}>📄</div>
                    <p style={{ color: 'var(--gray-500)', fontWeight: '500' }}>
                      Click or drag invoice file here
                    </p>
                    <p style={{ color: 'var(--gray-400)', fontSize: '0.85rem' }}>
                      Supports JPG, PNG, PDF
                    </p>
                  </>
                )}
              </div>
              {file && (
                <p style={{ fontSize: '0.85rem', color: 'var(--success)', marginTop: '0.5rem' }}>
                  ✓ {file.name} selected
                </p>
              )}
            </div>

            <button 
              type="submit" 
              className="btn btn-primary" 
              data-testid="scan-invoice-btn" 
              disabled={loading || !file}
              style={{ width: '100%' }}
            >
              {loading ? (
                <>
                  <span className="spinner" style={{ width: '18px', height: '18px', minHeight: 'auto' }}></span>
                  Scanning with AI...
                </>
              ) : (
                '🔍 Scan Invoice with AI'
              )}
            </button>
          </form>
        </div>

        {/* Results Section */}
        <div className="card" data-testid="scan-result">
          <h3 className="card-title">Extracted Data</h3>
          
          {!result && !loading && (
            <div className="empty-state">
              <div className="empty-state-icon">📋</div>
              <p className="empty-state-title">No data extracted yet</p>
              <p>Upload and scan an invoice to see extracted data</p>
            </div>
          )}

          {loading && (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <div className="spinner"></div>
              <p style={{ marginTop: '1rem', color: 'var(--gray-500)' }}>
                AI is analyzing the invoice...
              </p>
            </div>
          )}

          {result?.error && (
            <div className="alert alert-error">{result.error}</div>
          )}

          {result?.success && data && (
            <>
              {/* Quick Summary */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(2, 1fr)', 
                gap: '1rem',
                marginBottom: '1.5rem'
              }}>
                <div style={{ 
                  background: 'var(--gray-50)', 
                  padding: '1rem', 
                  borderRadius: '8px',
                  borderLeft: '4px solid var(--ptc-navy)'
                }}>
                  <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase', marginBottom: '0.25rem' }}>
                    Invoice Number
                  </p>
                  <p style={{ fontFamily: 'JetBrains Mono', fontWeight: '600', fontSize: '1.1rem', color: 'var(--ptc-navy)' }}>
                    {data.invoice_number || '-'}
                  </p>
                </div>
                <div style={{ 
                  background: 'var(--gray-50)', 
                  padding: '1rem', 
                  borderRadius: '8px',
                  borderLeft: '4px solid var(--ptc-red)'
                }}>
                  <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', textTransform: 'uppercase', marginBottom: '0.25rem' }}>
                    Total Amount
                  </p>
                  <p style={{ fontFamily: 'JetBrains Mono', fontWeight: '700', fontSize: '1.25rem', color: 'var(--ptc-red)' }}>
                    {formatCurrency(data.total_amount)}
                  </p>
                </div>
              </div>

              {/* Invoice Details */}
              <div style={{ marginBottom: '1.5rem' }}>
                <h4 style={{ fontSize: '0.9rem', fontWeight: '600', marginBottom: '0.75rem', color: 'var(--gray-700)' }}>
                  Invoice Details
                </h4>
                <table className="table" style={{ fontSize: '0.9rem' }}>
                  <tbody>
                    <tr>
                      <td style={{ fontWeight: '500', width: '40%' }}>Vendor</td>
                      <td>{data.vendor_name || '-'}</td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: '500' }}>Invoice Date</td>
                      <td>{data.invoice_date || '-'}</td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: '500' }}>Due Date</td>
                      <td>{data.due_date || '-'}</td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: '500' }}>Vehicle</td>
                      <td style={{ fontFamily: 'JetBrains Mono' }}>{data.vehicle_number || '-'}</td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: '500' }}>Subtotal</td>
                      <td style={{ fontFamily: 'JetBrains Mono' }}>{formatCurrency(data.subtotal)}</td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: '500' }}>GST ({data.gst_rate || '9%'})</td>
                      <td style={{ fontFamily: 'JetBrains Mono' }}>{formatCurrency(data.gst_amount)}</td>
                    </tr>
                    <tr style={{ background: 'var(--gray-50)' }}>
                      <td style={{ fontWeight: '700' }}>Total</td>
                      <td style={{ fontFamily: 'JetBrains Mono', fontWeight: '700', color: 'var(--ptc-red)' }}>
                        {formatCurrency(data.total_amount)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Line Items */}
              {data.line_items && data.line_items.length > 0 && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <h4 style={{ fontSize: '0.9rem', fontWeight: '600', marginBottom: '0.75rem', color: 'var(--gray-700)' }}>
                    Line Items ({data.line_items.length})
                  </h4>
                  <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    <table className="table" style={{ fontSize: '0.85rem' }}>
                      <thead>
                        <tr>
                          <th>Description</th>
                          <th style={{ textAlign: 'right' }}>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.line_items.map((item, idx) => (
                          <tr key={idx}>
                            <td>{item.description}</td>
                            <td style={{ textAlign: 'right', fontFamily: 'JetBrains Mono' }}>
                              {formatCurrency(item.amount)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Comparison with Repair */}
              {result.comparison && (
                <div style={{ 
                  background: result.comparison.variance > 100 ? 'var(--error-light)' : 
                             result.comparison.variance > 0 ? 'var(--warning-light)' : 'var(--success-light)',
                  padding: '1rem',
                  borderRadius: '8px',
                  marginBottom: '1.5rem'
                }}>
                  <h4 style={{ fontSize: '0.9rem', fontWeight: '600', marginBottom: '0.75rem' }}>
                    ⚖️ Comparison with Approved Quote
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem', fontSize: '0.85rem' }}>
                    <div>
                      <p style={{ color: 'var(--gray-600)' }}>Approved</p>
                      <p style={{ fontFamily: 'JetBrains Mono', fontWeight: '600' }}>
                        {formatCurrency(result.comparison.approved_amount)}
                      </p>
                    </div>
                    <div>
                      <p style={{ color: 'var(--gray-600)' }}>Invoice</p>
                      <p style={{ fontFamily: 'JetBrains Mono', fontWeight: '600' }}>
                        {formatCurrency(result.comparison.invoice_amount)}
                      </p>
                    </div>
                    <div>
                      <p style={{ color: 'var(--gray-600)' }}>Variance</p>
                      <p style={{ 
                        fontFamily: 'JetBrains Mono', 
                        fontWeight: '700',
                        color: result.comparison.variance > 0 ? 'var(--error)' : 'var(--success)'
                      }}>
                        {result.comparison.variance >= 0 ? '+' : ''}{formatCurrency(result.comparison.variance)}
                        <span style={{ fontSize: '0.75rem', marginLeft: '0.25rem' }}>
                          ({result.comparison.variance_percent}%)
                        </span>
                      </p>
                    </div>
                  </div>
                  {result.comparison.variance > 100 && (
                    <p style={{ marginTop: '0.75rem', fontSize: '0.8rem', color: 'var(--error)' }}>
                      ⚠️ Variance exceeds $100 - Will require Senior Manager approval
                    </p>
                  )}
                </div>
              )}

              {/* Actions */}
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                {repairId && (
                  <button
                    className="btn btn-success"
                    data-testid="export-excel-btn"
                    onClick={handleExportExcel}
                  >
                    📄 Export to Excel
                  </button>
                )}
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    // Copy extracted data to clipboard
                    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
                    alert('Data copied to clipboard!');
                  }}
                >
                  📋 Copy Data
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default InvoiceScanner;
